import { jwtDecode } from 'jwt-decode';

// 토큰 유틸리티 함수들
export const TokenManager = {
    // 백그라운드 갱신 상태 추적
    _refreshInProgress: false,
    _lastRefreshTime: 0,
    _refreshCooldown: 60000, // 1분 쿨다운

    // 사용자 권한 확인
    getUserRole() {
        const token = sessionStorage.getItem('token');
        if (!token) return null;

        try {
            const decoded = jwtDecode(token);
            return decoded.role;
        } catch (error) {
            return null;
        }
    },

    // 사용자명 추출 (refresh 시 필요)
    getUsername() {
        const token = sessionStorage.getItem('token');
        if (!token) return null;

        try {
            const decoded = jwtDecode(token);
            // JWT에서 username 필드 확인 (sub, username, user_id 등 가능)
            return decoded.sub || decoded.username || decoded.user_id || null;
        } catch (error) {
            return null;
        }
    },

    // siteToken 유효성 검사
    isSiteTokenValid() {
        const siteToken = sessionStorage.getItem('siteToken');
        if (!siteToken) return false;

        try {
            const decoded = jwtDecode(siteToken);
            const currentTime = Math.floor(Date.now() / 1000);
            return decoded.exp > currentTime;
        } catch (error) {
            return false;
        }
    },

    // token 유효성 검사
    isTokenValid() {
        const token = sessionStorage.getItem('token');
        if (!token) return false;

        try {
            const decoded = jwtDecode(token);
            const currentTime = Math.floor(Date.now() / 1000);
            return decoded.exp > currentTime;
        } catch (error) {
            return false;
        }
    },

    // 토큰이 곧 만료되는지 확인 (5분 이내)
    isTokenExpiringsoon(tokenType = 'token') {
        const tokenItem = sessionStorage.getItem(tokenType);
        if (!tokenItem) return false;

        try {
            const decoded = jwtDecode(tokenItem);
            const currentTime = Math.floor(Date.now() / 1000);
            const expiryTime = decoded.exp;
            const fiveMinutesInSeconds = 5 * 60; // 5분

            // 만료 시간이 5분 이내인 경우 true 반환
            return (expiryTime - currentTime) <= fiveMinutesInSeconds;
        } catch (error) {
            return false;
        }
    },

    // 토큰 만료까지 남은 시간 (초 단위)
    getTokenTimeUntilExpiry(tokenType = 'token') {
        const tokenItem = sessionStorage.getItem(tokenType);
        if (!tokenItem) return 0;

        try {
            const decoded = jwtDecode(tokenItem);
            const currentTime = Math.floor(Date.now() / 1000);
            const expiryTime = decoded.exp;

            return Math.max(0, expiryTime - currentTime);
        } catch (error) {
            return 0;
        }
    },

    // 경로별 적절한 토큰 반환
    getAppropriateToken(pathname) {
        const userRole = this.getUserRole();

        // SA 유저의 경우 경로별 토큰 사용 규칙 적용
        if (userRole === 'SA') {
            if (pathname.includes('/safedb/common/') || pathname.startsWith('/safedb/common/')) {
                // SA 유저가 /safedb/common/* 경로에 접근하는 경우
                // 항상 siteToken 사용해야 함
                const siteToken = sessionStorage.getItem('siteToken');
                const token = sessionStorage.getItem('token');

                // siteToken 사용 중에도 백그라운드에서 token 상태 확인
                if (token && this.isTokenExpiringsoon('token')) {
                    // 백그라운드 갱신 중복 방지
                    const now = Date.now();
                    if (!this._refreshInProgress && (now - this._lastRefreshTime) > this._refreshCooldown) {
                        this._refreshInProgress = true;
                        this._lastRefreshTime = now;

                        // 백그라운드에서 token 갱신 (비동기로 처리하되 결과를 기다리지 않음)
                        this.refreshToken(true).catch(() => {
                            // 백그라운드 갱신 실패 시 조용히 실패
                        }).finally(() => {
                            this._refreshInProgress = false;
                        });
                    }
                }

                if (siteToken && this.isSiteTokenValid()) {
                    return { token: siteToken, type: 'siteToken' };
                } else {
                    // siteToken이 없거나 만료된 경우
                    throw new Error('SA_COMMON_NO_SITE_TOKEN');
                }
            } else if (pathname.includes('/safedb/SA/') || pathname.startsWith('/safedb/SA/')) {
                // SA 유저가 /safedb/SA/* 경로에 접근하는 경우
                // siteToken이 존재하면 제거하고 token만 사용
                const token = sessionStorage.getItem('token');
                const siteToken = sessionStorage.getItem('siteToken');

                if (siteToken) {
                    // siteToken을 실제로 제거
                    sessionStorage.removeItem('siteToken');
                    throw new Error('SA_AREA_WITH_SITE_TOKEN');
                }

                if (token && this.isTokenValid()) {
                    return { token: token, type: 'token' };
                } else {
                    throw new Error('INVALID_TOKEN');
                }
            }
        }

        // 기존 로직: siteToken이 있으면 사용하고, 없으면 token 사용
        const siteToken = sessionStorage.getItem('siteToken');
        const token = sessionStorage.getItem('token');

        if (siteToken && this.isSiteTokenValid()) {
            return { token: siteToken, type: 'siteToken' };
        } else if (token && this.isTokenValid()) {
            return { token: token, type: 'token' };
        } else {
            throw new Error('NO_VALID_TOKEN');
        }
    },

    // 토큰 갱신 함수 (백그라운드용)
    async refreshToken(isBackground = false) {
        const refreshToken = sessionStorage.getItem('refreshToken');
        if (!refreshToken) {
            throw new Error('REFRESH_TOKEN_NOT_FOUND');
        }

        try {
            const username = this.getUsername();
            if (!username) {
                throw new Error('USERNAME_NOT_FOUND_IN_TOKEN');
            }

            const authUrl = process.env.REACT_APP_AUTH_URL || 'https://auth.safedb.biz';
            const response = await fetch(`${authUrl}/auth/refresh`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    refreshToken,
                    username
                })
            });

            if (!response.ok) {
                throw new Error('TOKEN_REFRESH_FAILED');
            }

            const data = await response.json();
            const newToken = data.token;

            // 새 토큰 저장
            sessionStorage.setItem('token', newToken);

            return newToken;
        } catch (error) {

            // 백그라운드 갱신이 아닐 때만 리다이렉트
            if (!isBackground) {
                // 갱신 실패 시 모든 토큰 제거 후 로그인 페이지로 이동
                sessionStorage.removeItem('token');
                sessionStorage.removeItem('refreshToken');
                sessionStorage.removeItem('siteToken');
                window.location.href = '/authentication/simple/login';
            }

            throw new Error('TOKEN_REFRESH_FAILED');
        }
    },

    // Authorization 헤더 생성
    createAuthHeader(pathname) {
        const result = this.getAppropriateToken(pathname);
        return {
            Authorization: `Bearer ${result.token}`,
            tokenType: result.type // 어떤 토큰을 사용했는지 정보 추가
        };
    }
};
